import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _toLower from 'lodash/toLower';

/**
 * @param {Array} data
 * @param {Object} tools
 * @param {number} tools.depth
 * @param {boolean} tools.isMulti
 * @param {(dataset: any) => string} tools.getLabel
 * @param {(dataset: any) => string} tools.getValue
 * @param {(dataset: any) => number} tools.getOptions
 * @param {(dataset: any) => boolean} tools.checkIsSelected
 * @param {(dataset: any) => boolean} tools.checkIsDisabled
 * @param {(dataset: any) => boolean} tools.checkIfSkipped
 * @returns {{
 *   value: import("utils/types").SelectOption | import("utils/types").SelectOption[],
 *   options: import("utils/types").SelectOption[],
 *   optionsMap: Record<string, {label: string, value: string, depth: number}>
 * }}
 */
export function createOptions(
  data = [],
  {
    depth = 0,
    isMulti = false,
    getLabel = dataset => _get(dataset, 'label', ''),
    getValue = dataset => _get(dataset, 'value', ''),
    getOptions = dataset => _get(dataset, 'options', []),
    checkIsSelected = () => false,
    checkIsDisabled = () => false,
    checkIfSkipped = () => false
  } = {}
) {
  const result = { options: [], optionsMap: {}, value: isMulti ? [] : null };

  if (!_isArray(data)) return result;

  return data.reduce((acc, item) => {
    const itemLabel = getLabel(item);
    const itemValue = getValue(item);
    const itemOptions = getOptions(item);
    const isItemSelected = checkIsSelected(item);
    const isItemDisabled = checkIsDisabled(item);
    const isItemSkipped = checkIfSkipped(item);

    if (itemValue && !isItemSkipped) {
      const info = {
        ...item,
        depth,
        label: itemLabel,
        value: itemValue,
        isDisabled: isItemDisabled
      };
      const sub = createOptions(itemOptions, {
        depth: depth + 1,
        isMulti,
        getLabel,
        getValue,
        getOptions,
        checkIsSelected,
        checkIsDisabled,
        checkIfSkipped
      });
      if (_isEmpty(sub.options)) {
        acc.options.push(info);
      } else {
        acc.options.push({ ...info, options: sub.options });
      }
      acc.optionsMap = {
        ...acc.optionsMap,
        [itemValue]: info,
        ...sub.optionsMap
      };
      if (isItemSelected) {
        acc.value = isMulti ? [...acc.value, info, ...sub.value] : info;
      } else {
        acc.value = isMulti ? [...acc.value, ...sub.value] : sub.value;
      }
    }

    return acc;
  }, result);
}

/**
 * @param {import("utils/types").SelectOption[]} options
 * @param {number} [depth]
 * @return {import("utils/types").SelectOption[]}
 */
export function createAllOptions(options, depth = 0) {
  if (!_isArray(options)) return [];

  return options.reduce((acc, option) => {
    const { label, value, options, ...data } = option;

    if (value) {
      const query = createSearchQuery(option);
      acc.push({ ...data, label, value, depth, query });
    }
    if (options) {
      acc.push(...createAllOptions(options, depth + 1));
    }
    return acc;
  }, []);
}

/**
 * @param {import("utils/types").SelectOption} option
 */
function createSearchQuery(option) {
  const label = _get(option, 'label', '');
  const subOptions = _get(option, 'options', []);
  let query = label;

  if (_isArray(subOptions) && !_isEmpty(subOptions)) {
    const subQueries = subOptions.map(createSearchQuery);
    query += ` ${subQueries.join(' ')}`;
  }

  return _toLower(query).trim();
}
